import { useNavigation } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext";
import { Plane, Key, Settings, Compass, ChevronRight, AlertTriangle } from "lucide-react";

const ListingCategoryForm = () => {
  const { navigateTo } = useNavigation();
  const { getDictValue, enabledMenuFunctions } = useAuth();

  // Configurație fixă pentru rute, iconițe și culori asociate acțiunilor din baza de date
  const categoryMetaMap: Record<string, { target: string; icon: React.ComponentType<any>; color: string }> = {
    aircraft: {
      target: "list-aircraft",
      icon: Plane,
      color: "text-blue-400 group-hover:bg-blue-500/10 border-blue-500/20"
    },
    rental: {
      target: "rent-aircraft",
      icon: Key,
      color: "text-emerald-400 group-hover:bg-emerald-500/10 border-emerald-500/20"
    },
    part: {
      target: "list-parts",
      icon: Settings,
      color: "text-amber-400 group-hover:bg-amber-500/10 border-amber-500/20"
    },
    experience: {
      target: "list-experience",
      icon: Compass,
      color: "text-purple-400 group-hover:bg-purple-500/10 border-purple-500/20"
    }
  };

  // Generăm dinamic lista de categorii active pe baza `enabledMenuFunctions` (unde type === "Menu")
  const activeCategories = enabledMenuFunctions
    .filter((func) => func.action && categoryMetaMap[func.action.toLowerCase()])
    .map((func) => {
      const actionKey = func.action.toLowerCase();
      const meta = categoryMetaMap[actionKey];

      return {
        target: meta.target,
        icon: meta.icon,
        color: meta.color,
        // Construim dinamic cheile de traducere cerute
        title: getDictValue(`submenu.label.${actionKey}`),
        description: getDictValue(`sell.${actionKey}.description`)
      };
    });

  // Traduceri globale pentru header și footer din dicționar
  const sellHeader = getDictValue("sell.header");
  const sellTitle = getDictValue("sell.title");
  const sellDescription = getDictValue("sell.description");

  return (
    <div className="min-h-[85vh] flex items-center justify-center py-12 px-6 bg-[#020617] font-sans">
      <div className="w-full max-w-2xl space-y-8">

        {/* HEADER DINAMIC */}
        <header className="border-l-4 border-blue-600 pl-5 space-y-1">
          <p className="text-blue-400 font-mono text-[10px] font-bold tracking-[0.25em]">
            {sellHeader}
          </p>
          <h1 className="text-3xl font-black text-white tracking-tight uppercase italic">
            {sellTitle}
          </h1>
          <p className="text-xs text-slate-400">
            {sellDescription}
          </p>
        </header>

        {/* LISTĂ AUTOMATĂ DINAMICĂ FILTRATĂ */}
        <div className="flex flex-col gap-3">
          {activeCategories.map((cat, index) => {
            const Icon = cat.icon;
            return (
              <button
                key={`${cat.target}-${index}`}
                onClick={() => navigateTo(cat.target as any)}
                className="group w-full flex items-center justify-between p-4 rounded-2xl border border-slate-800/80 bg-[#0F172A]/40 backdrop-blur-xl hover:border-slate-700 hover:bg-[#0F172A]/80 transition-all duration-300 text-left active:scale-[0.99] shadow-lg relative overflow-hidden"
              >
                {/* Conținut Stânga: Icon + Traducere injectată dinamic */}
                <div className="flex items-center gap-4 z-10">
                  <div className={`p-3 rounded-xl bg-slate-900/80 border border-slate-800 transition-colors duration-300 ${cat.color}`}>
                    <Icon size={18} className="transition-transform duration-300 group-hover:scale-110" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white uppercase tracking-wide italic leading-none mb-1">
                      {cat.title}
                    </h3>
                    <p className="text-[11px] text-slate-400 tracking-normal font-medium max-w-md sm:max-w-none line-clamp-1 transition-colors group-hover:text-slate-300">
                      {cat.description}
                    </p>
                  </div>
                </div>

                {/* Săgeată Dreapta cu micro-animație */}
                <div className="p-1.5 rounded-lg bg-slate-900/40 border border-slate-800/60 opacity-30 group-hover:opacity-100 group-hover:border-slate-700 transition-all duration-300 transform group-hover:translate-x-0.5 z-10 shrink-0 ml-4">
                  <ChevronRight size={14} className="text-white" />
                </div>
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default ListingCategoryForm;